/* global axios */
import ApiClient from './ApiClient';

/**
 * KanbanAPI - Optimized API client for Kanban operations
 *
 * This API is specifically designed for high-performance Kanban/Funnel operations:
 * - Optimized stage loading with pagination
 * - Load more functionality per stage
 * - Fast move operations with minimal data
 * - Support for filtering and sorting
 */
class KanbanAPI extends ApiClient {
  constructor() {
    super('funnels', {
      accountScoped: true,
    });
  }

  /**
   * Get stage conversations with pagination (optimized for Kanban)
   * Returns all stages with limited conversations per stage
   *
   * @param {number} funnelId - Funnel ID
   * @param {Object} options - Query options
   * @param {number} options.perStage - Conversations per stage (default: 20)
   * @param {string} options.status - Filter by status
   * @param {number} options.assigneeId - Filter by assignee
   * @param {number} options.inboxId - Filter by inbox
   * @param {string} options.sortBy - Sort field (updated_at, created_at, stage_entered_at)
   * @param {string} options.sortOrder - Sort order (asc, desc)
   * @returns {Promise} Response with stages and conversations
   */
  getStageConversations(funnelId, options = {}) {
    const {
      perStage = 20,
      status,
      assigneeId,
      inboxId,
      sortBy,
      sortOrder,
    } = options;

    const params = {
      per_stage: perStage,
    };

    if (status) params.status = status;
    if (assigneeId) params.assignee_id = assigneeId;
    if (inboxId) params.inbox_id = inboxId;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;

    return axios.get(`${this.url}/${funnelId}/kanban/stage_conversations`, {
      params,
    });
  }

  exportLeads(funnelId, params = {}) {
    return axios.get(`${this.url}/${funnelId}/kanban/export_leads`, {
      params,
      responseType: 'blob',
    });
  }

  getCrmSummary(funnelId, params = {}) {
    return axios.get(`${this.url}/${funnelId}/kanban/crm_summary`, {
      params,
    });
  }

  /**
   * Load more conversations for a specific stage
   * Used for infinite scroll / lazy loading in Kanban columns
   *
   * @param {number} funnelId - Funnel ID
   * @param {string} stageName - Stage name
   * @param {Object} options - Pagination options
   * @param {number} options.page - Page number (default: 2)
   * @param {number} options.perPage - Items per page (default: 20)
   * @returns {Promise} Response with conversations and pagination info
   */
  loadMoreStage(funnelId, stageName, options = {}) {
    const { page = 2, perPage = 20 } = options;

    return axios.get(`${this.url}/${funnelId}/kanban/load_more_stage`, {
      params: {
        stage_name: stageName,
        page,
        per_page: perPage,
      },
    });
  }

  /**
   * Move conversation to a different stage
   * Optimized for fast response - returns minimal data
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {string} stageName - Target stage name
   * @returns {Promise} Response with success status
   */
  moveConversation(funnelId, conversationId, stageName) {
    return axios.post(`${this.url}/${funnelId}/kanban/move_conversation`, {
      conversation_id: conversationId,
      stage_name: stageName,
    });
  }

  /**
   * Get conversations for a specific stage with full pagination
   * Used when viewing a single stage or filtering
   *
   * @param {number} funnelId - Funnel ID
   * @param {Object} options - Query options
   * @param {string} options.stageName - Stage name
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.perPage - Items per page (default: 20)
   * @param {boolean} options.includeCounts - Include total counts (default: false)
   * @param {string} options.status - Filter by status
   * @param {number} options.assigneeId - Filter by assignee
   * @returns {Promise} Response with conversations and meta
   */
  getConversations(funnelId, options = {}) {
    const {
      stageName,
      page = 1,
      perPage = 20,
      includeCounts = false,
      status,
      assigneeId,
    } = options;

    const params = {
      page,
      per_page: perPage,
    };

    if (stageName) params.stage_name = stageName;
    if (includeCounts) params.include_counts = 'true';
    if (status) params.status = status;
    if (assigneeId) params.assignee_id = assigneeId;

    return axios.get(`${this.url}/${funnelId}/kanban/conversations`, {
      params,
    });
  }

  /**
   * Mark a deal as won
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {Object} data - Win data
   * @param {number} data.final_value - Final deal value
   * @param {string} data.close_date - Close date (YYYY-MM-DD)
   * @param {string} data.notes - Optional notes
   * @returns {Promise} Response with updated mapping
   */
  markDealAsWon(funnelId, conversationId, data = {}) {
    return axios.post(`${this.url}/${funnelId}/kanban/mark_deal_won`, {
      conversation_id: conversationId,
      final_value: data.final_value,
      close_date: data.close_date,
      notes: data.notes,
      won_reason: data.won_reason,
      won_reason_id: data.won_reason_id,
    });
  }

  /**
   * Mark a deal as lost
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {Object} data - Loss data
   * @param {string} data.reason - Loss reason
   * @param {string} data.reason_id - Loss reason ID
   * @param {string} data.notes - Optional notes
   * @returns {Promise} Response with updated mapping
   */
  markDealAsLost(funnelId, conversationId, data = {}) {
    return axios.post(`${this.url}/${funnelId}/kanban/mark_deal_lost`, {
      conversation_id: conversationId,
      reason: data.reason,
      reason_id: data.reason_id,
      notes: data.notes,
    });
  }

  reopenDeal(funnelId, conversationId) {
    return axios.post(`${this.url}/${funnelId}/kanban/reopen_deal`, {
      conversation_id: conversationId,
    });
  }

  /**
   * Update deal value
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {number} value - New deal value
   * @returns {Promise} Response with updated mapping
   */
  updateDealValue(funnelId, conversationId, value) {
    return axios.patch(`${this.url}/${funnelId}/kanban/update_deal`, {
      conversation_id: conversationId,
      deal_value: value,
    });
  }

  /**
   * Update deal fields (expected close date, etc) via update_deal
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation display ID
   * @param {Object} data - Fields to update (e.g. expected_close_date)
   * @returns {Promise} Response with updated mapping
   */
  updateDealMetadata(funnelId, conversationId, data = {}) {
    return axios.patch(`${this.url}/${funnelId}/kanban/update_deal`, {
      conversation_id: conversationId,
      ...data,
    });
  }

  /**
   * Create a PIX charge (Asaas) for a deal
   *
   * @param {number} funnelId - Funnel ID
   * @param {Object} payload - Charge payload
   * @param {number} payload.conversationId - Conversation display ID
   * @param {number} payload.value - Charge value
   * @param {string} [payload.dueDate] - Optional due date (YYYY-MM-DD)
   * @param {string} [payload.description] - Optional description
   * @returns {Promise} Response with pix_payload, pix_qr_code, status, etc
   */
  createDealPayment(funnelId, { conversationId, value, dueDate, description }) {
    return axios.post(`${this.url}/${funnelId}/kanban/create_deal_payment`, {
      conversation_id: conversationId,
      value,
      due_date: dueDate,
      description,
    });
  }

  /**
   * Update deal probability
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {number} probability - New probability (0-100)
   * @returns {Promise} Response with updated mapping
   */
  updateDealProbability(funnelId, conversationId, probability) {
    return axios.patch(`${this.url}/${funnelId}/kanban/update_deal`, {
      conversation_id: conversationId,
      probability: probability,
    });
  }

  /**
   * Update checklist item
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {string} itemId - Checklist item ID
   * @param {Object} updates - Updates (done, text, etc)
   * @returns {Promise} Response with updated mapping
   */
  updateChecklistItem(funnelId, conversationId, itemId, updates) {
    return axios.patch(`${this.url}/${funnelId}/kanban/update_checklist_item`, {
      conversation_id: conversationId,
      item_id: itemId,
      ...updates,
    });
  }

  /**
   * Add a note to the deal
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @param {string} content - Note content
   * @returns {Promise} Response with created activity
   */
  addDealNote(funnelId, conversationId, content) {
    return axios.post(`${this.url}/${funnelId}/kanban/add_note`, {
      conversation_id: conversationId,
      content: content,
    });
  }

  /**
   * Get deal activities/history
   *
   * @param {number} funnelId - Funnel ID
   * @param {number} conversationId - Conversation ID
   * @returns {Promise} Response with activities
   */
  /**
   * Transfer conversation to another funnel
   *
   * @param {number} funnelId - Source funnel ID
   * @param {number} conversationId - Conversation display ID
   * @param {number} targetFunnelId - Target funnel ID
   * @param {string} targetStageName - Target stage name
   * @param {boolean} removeFromSource - Remove from source funnel (default: true)
   * @returns {Promise} Response with new mapping
   */
  transferToFunnel(
    funnelId,
    conversationId,
    targetFunnelId,
    targetStageName,
    removeFromSource = true
  ) {
    return axios.post(`${this.url}/${funnelId}/kanban/transfer_to_funnel`, {
      conversation_id: conversationId,
      target_funnel_id: targetFunnelId,
      target_stage_name: targetStageName,
      remove_from_source: removeFromSource,
    });
  }

  updateLeadTemperature(funnelId, conversationId, temperature) {
    return axios.patch(
      `${this.url}/${funnelId}/kanban/update_lead_temperature`,
      {
        conversation_id: conversationId,
        lead_temperature: temperature,
      }
    );
  }

  getDealActivities(funnelId, conversationId) {
    return axios.get(`${this.url}/${funnelId}/kanban/activities`, {
      params: {
        conversation_id: conversationId,
      },
    });
  }
}

export default new KanbanAPI();
